import React, { useState, useContext, createContext } from 'react'
import { Input } from '~/lib/ui/input'
import { Textarea } from '~/lib/ui/textarea'
import { Button } from '~/lib/ui/button'
import { PencilSquareIcon } from '@heroicons/react/24/outline'

const EditingContext = createContext(false)
export const useIsEditing = () => useContext(EditingContext)
const EditingProvider = ({ children }) => {
  const [isEditing, setIsEditing] = useState(false)

  return (
    <EditingContext.Provider value={{ isEditing, setIsEditing }}>
      {children}
    </EditingContext.Provider>
  )
}

export const Panel = ({ children }) => {
  return (
    <div className="flex w-[55rem] flex-col items-center gap-y-6 font-roboto">
      <div className="flex w-full flex-col gap-y-12 rounded-2xl border border-silver bg-white p-6 shadow-md">
        <EditingProvider>{children}</EditingProvider>
      </div>
    </div>
  )
}

export const EditSaveCancelButtons = ({
  sectionName,
  saveClick,
  cancelClick,
}) => {
  const { isEditing, setIsEditing } = useIsEditing()
  const handleSaveClick = (e) => {
    saveClick(e)
    setIsEditing(false)
  }
  const handleCancelClick = (e) => {
    cancelClick(e)
    setIsEditing(false)
  }

  return isEditing ? (
    <SaveCancelButtons
      saveClick={handleSaveClick}
      cancelClick={handleCancelClick}
    />
  ) : (
    <EditButton sectionName={sectionName} onClick={() => setIsEditing(true)} />
  )
}

export const EditButton = ({ sectionName, onClick }) => {
  return (
    <Button size="sm" onClick={onClick}>
      <PencilSquareIcon className="mr-4 inline-block size-4" />
      Edit {sectionName}
    </Button>
  )
}

export const SaveCancelButtons = ({ saveClick, cancelClick }) => {
  return (
    <div className="flex flex-row gap-x-3">
      <Button
        variant="outline"
        size="sm"
        className="px-4"
        onClick={cancelClick}
      >
        Cancel
      </Button>
      <Button size="sm" className="px-4" onClick={saveClick}>
        Save Changes
      </Button>
    </div>
  )
}

const FormDataContext = createContext({})
export const useFormData = () => useContext(FormDataContext)

export const FormDataProvider = ({ formData, setFormData, children }) => {
  const updateDataField = (field) => (e) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }
  const valueFor = (field) => formData[field] || ''
  const value = { valueFor, updateDataField }
  return (
    <FormDataContext.Provider value={value}>
      {children}
    </FormDataContext.Provider>
  )
}

export const IconTextField = ({
  icon: Icon,
  placeholder,
  label,
  name,
  ...props
}) => {
  const { valueFor, updateDataField } = useFormData()
  const { isEditing } = useIsEditing()
  return (
    <div>
      <label htmlFor={name} className="pb-3 font-medium text-intoo-text">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-2 z-10 text-[#717182]">
          <Icon className="size-5" />
        </div>
        <Input
          placeholder={placeholder}
          value={valueFor(name)}
          onChange={updateDataField(name)}
          name={name}
          className="h-10 pl-10"
          disabled={!isEditing}
          {...props}
        />
      </div>
    </div>
  )
}

export const TextareaField = ({ placeholder, label, name, ...props }) => {
  const { valueFor, updateDataField } = useFormData()
  const { isEditing } = useIsEditing()
  return (
    <div className="flex flex-col gap-y-3">
      <label htmlFor="bio" className="font-medium text-intoo-text">
        {label}
      </label>
      <Textarea
        placeholder="Tell us about yourself..."
        className="h-22.5"
        name={name}
        value={valueFor(name)}
        onChange={updateDataField(name)}
        disabled={!isEditing}
        {...props}
      />
    </div>
  )
}

export const TextField = ({ placeholder, label, name, ...props }) => {
  const { valueFor, updateDataField } = useFormData()
  const { isEditing } = useIsEditing()
  return (
    <div>
      <label htmlFor={name} className="pb-3 font-medium text-intoo-text">
        {label}
      </label>
      <Input
        placeholder={placeholder}
        value={valueFor(name)}
        onChange={updateDataField(name)}
        name={name}
        className="h-10"
        disabled={!isEditing}
        {...props}
      />
    </div>
  )
}
